import React, { useEffect, useState } from 'react';
import { payrollAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { money } from './_util';
import { EmployeePicker } from './EmployeePicker';

export const DeductionsSection = () => {
  const [codes, setCodes] = useState([]);
  const [code, setCode] = useState({ code: '401k', name: '401(k)', kind: 'pre_tax', reduces: 'federal_income,state_income' });
  const [elect, setElect] = useState({ employee_id: '', code: '401k', effective_date: '2026-01-01', amount: '', percent: '' });
  const [garnEmp, setGarnEmp] = useState('');
  const [garns, setGarns] = useState([]);
  const [garn, setGarn] = useState({ employee_id: '', type: 'child_support', case_number: '', payee: '', priority: '1', amount: '', percent: '', cap: '', state: 'OK' });

  const loadCodes = () => payrollAPI.deductionCodes().then((r) => setCodes(r.data.codes || [])).catch(() => {});
  useEffect(() => { loadCodes(); }, []);

  const saveCode = async () => {
    try {
      await payrollAPI.putDeductionCode({ ...code, reduces: code.reduces.split(',').map((s) => s.trim()).filter(Boolean) });
      toast.success('Deduction code saved'); loadCodes();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save code'); }
  };

  const addElection = async () => {
    try {
      await payrollAPI.addElection({
        employee_id: elect.employee_id, code: elect.code, effective_date: elect.effective_date,
        amount: elect.amount ? Number(elect.amount) : null, percent: elect.percent ? Number(elect.percent) : null,
      });
      toast.success('Election added');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add election'); }
  };

  const loadGarns = async () => {
    if (!garnEmp.trim()) return;
    try { const r = await payrollAPI.garnishments(garnEmp.trim()); setGarns(r.data.garnishments || []); }
    catch { toast.error('Lookup failed'); }
  };

  const addGarn = async () => {
    try {
      await payrollAPI.addGarnishment({
        ...garn, priority: Number(garn.priority),
        amount: garn.amount ? Number(garn.amount) : null, percent: garn.percent ? Number(garn.percent) : null,
        cap: garn.cap ? Number(garn.cap) : null,
      });
      toast.success('Garnishment added'); setGarnEmp(garn.employee_id); loadGarns();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add garnishment'); }
  };

  return (
    <div className="space-y-6">
      <Card data-testid="netpay-deductions">
        <CardHeader><CardTitle>Deduction codes & elections</CardTitle>
          <CardDescription>Pre-tax, post-tax and employer codes. Pre-tax codes name the taxes they reduce. Elections set a flat amount or percent; loan goals stop at zero.</CardDescription></CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-auto max-h-56 border rounded text-sm">
            <table className="w-full"><thead><tr className="text-left text-xs text-slate-500"><th className="p-2">Code</th><th>Name</th><th>Kind</th><th>Reduces</th></tr></thead>
              <tbody>{codes.map((c) => (<tr key={c.code} className="border-t" data-testid={`ded-code-${c.code}`}><td className="p-2">{c.code}</td><td>{c.name}</td><td><Badge variant="outline">{c.kind}</Badge></td><td className="text-xs text-slate-500">{(c.reduces || []).join(', ')}</td></tr>))}
                {!codes.length && <tr><td colSpan={4} className="p-3 text-slate-500">No codes yet.</td></tr>}</tbody></table>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <label className="text-sm">Code<Input data-testid="ded-code-input" value={code.code} onChange={(e) => setCode({ ...code, code: e.target.value })} /></label>
            <label className="text-sm">Name<Input data-testid="ded-name" value={code.name} onChange={(e) => setCode({ ...code, name: e.target.value })} /></label>
            <label className="text-sm">Kind<Input data-testid="ded-kind" value={code.kind} onChange={(e) => setCode({ ...code, kind: e.target.value })} placeholder="pre_tax | post_tax | employer" /></label>
            <label className="text-sm">Reduces<Input data-testid="ded-reduces" value={code.reduces} onChange={(e) => setCode({ ...code, reduces: e.target.value })} /></label>
            <Button data-testid="ded-save" onClick={saveCode}>Save code</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-start border-t pt-3">
            <div className="text-sm md:col-span-2"><span className="block mb-1">Employee</span>
              <EmployeePicker testid="elect-emp" value={elect.employee_id}
                onChange={(e) => setElect({ ...elect, employee_id: e.id })} /></div>
            <label className="text-sm">Code<Input data-testid="elect-code" value={elect.code} onChange={(e) => setElect({ ...elect, code: e.target.value })} /></label>
            <label className="text-sm">Amount ($)<Input data-testid="elect-amount" value={elect.amount} onChange={(e) => setElect({ ...elect, amount: e.target.value })} /></label>
            <label className="text-sm">Percent<Input data-testid="elect-percent" value={elect.percent} onChange={(e) => setElect({ ...elect, percent: e.target.value })} /></label>
            <Button data-testid="elect-add" disabled={!elect.employee_id} onClick={addElection}>Add election</Button>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="netpay-garnishments">
        <CardHeader><CardTitle>Garnishments (CCPA limits)</CardTitle>
          <CardDescription>Child support, tax levy, student loan, creditor and bankruptcy orders — ordered by priority and capped by the federal CCPA limits (and any state cap) at calc time.</CardDescription></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-2 md:items-end">
            <div className="text-sm md:w-96"><span className="block mb-1">Look up garnishments for</span>
              <EmployeePicker testid="garn-lookup-emp" value={garnEmp}
                onChange={(e) => { setGarnEmp(e.id); setGarns([]); }} /></div>
            <Button variant="outline" data-testid="garn-lookup" disabled={!garnEmp} onClick={loadGarns}>Look up</Button>
          </div>
          {garns.length > 0 && (
            <div className="overflow-auto max-h-56 border rounded text-sm">
              <table className="w-full"><thead><tr className="text-left text-xs text-slate-500"><th className="p-2">Priority</th><th>Type</th><th>Case</th><th>Payee</th><th>Amount</th><th>Cap</th></tr></thead>
                <tbody>{garns.map((g) => (<tr key={g.id} className="border-t" data-testid={`garn-row-${g.id}`}><td className="p-2">{g.priority}</td><td>{g.type}</td><td>{g.case_number}</td><td>{g.payee}</td><td>{money(g.amount_cents)}</td><td>{money(g.cap_cents)}</td></tr>))}</tbody></table>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end border-t pt-3">
            <div className="text-sm md:col-span-2"><span className="block mb-1">Employee</span>
              <EmployeePicker testid="garn-emp" value={garn.employee_id}
                onChange={(e) => setGarn({ ...garn, employee_id: e.id })} /></div>
            <label className="text-sm">Type<Input data-testid="garn-type" value={garn.type} onChange={(e) => setGarn({ ...garn, type: e.target.value })} /></label>
            <label className="text-sm">Case #<Input data-testid="garn-case" value={garn.case_number} onChange={(e) => setGarn({ ...garn, case_number: e.target.value })} /></label>
            <label className="text-sm">Payee<Input data-testid="garn-payee" value={garn.payee} onChange={(e) => setGarn({ ...garn, payee: e.target.value })} /></label>
            <label className="text-sm">Priority<Input data-testid="garn-priority" value={garn.priority} onChange={(e) => setGarn({ ...garn, priority: e.target.value })} /></label>
            <label className="text-sm">Amount ($)<Input data-testid="garn-amount" value={garn.amount} onChange={(e) => setGarn({ ...garn, amount: e.target.value })} /></label>
            <label className="text-sm">State cap ($)<Input data-testid="garn-cap" value={garn.cap} onChange={(e) => setGarn({ ...garn, cap: e.target.value })} /></label>
            <Button data-testid="garn-add" disabled={!garn.employee_id} onClick={addGarn}>Add garnishment</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
